import {
  mediaQueryLarge,
  requestIdleCallback,
  startViewTransition,
} from "@theme/utilities";
import PaginatedList from "@theme/paginated-list";

/** @param {string} str */
function esc(str) {
  const el = document.createElement("span");
  el.textContent = str;
  return el.innerHTML;
}

/**
 * Builds a value-scale gauge SVG for warmth/durability/mobility.
 *
 * @param {number} value
 * @param {string} label
 * @returns {string}
 */
function buildValueScale(value, label) {
  const max = 8;
  const total = 24;
  const filled = value * 3;
  let rects = "";

  for (let i = 0; i < total; i++) {
    const angle = i * 15;
    const opacity = i < filled ? 1 : 0.2;
    rects += `<rect x="24.09" y="0" width="2.82" height="5.64" rx="1.41" fill="var(--ui-primary)" opacity="${opacity}" transform="rotate(${angle} 25.5 25.5)"/>`;
  }

  return `<div class="value-scale product-card-large" aria-label="${label} ${value}/${max}">
    <div class="value-scale__gauge">
      <svg class="value-scale__svg" viewBox="0 0 51 51" fill="none" xmlns="http://www.w3.org/2000/svg">${rects}</svg>
      <span class="value-scale__center text-secondary-12-120">${value}/${max}</span>
    </div>
    <div class="value-scale__labels">
      <div class="value-scale__title-row">
        <span class="value-scale__title text-secondary-12-120 color-secondary">${label}</span>
      </div>
    </div>
  </div>`;
}

/**
 * Builds a presentation card from the sibling default card and data attributes.
 *
 * @param {Element} placeholder
 */
function hydratePresentation(placeholder) {
  const wrapper = placeholder.closest("li") || placeholder.parentElement;
  const defaultView = wrapper?.querySelector(".product-card-default-view");
  const defaultCard = defaultView?.querySelector("product-card");

  if (!defaultCard) return;

  const productId = defaultCard.dataset.productId || "";
  const titleLink = defaultCard.querySelector(".product-card-title-link");
  const title = titleLink?.textContent?.trim() || "";
  const url = titleLink?.getAttribute("href") || "";
  const price =
    defaultCard
      .querySelector(".product-card-price-value")
      ?.textContent?.trim() || "";
  const img = defaultCard.querySelector(".product-card-image.first");

  let imageHtml = "";
  if (img) {
    const clone = /** @type {HTMLImageElement} */ (img.cloneNode(false));
    clone.className = "product-card-image first";
    clone.loading = "lazy";
    clone.removeAttribute("fetchpriority");
    imageHtml = clone.outerHTML;
  }

  const uspItems = defaultCard.querySelectorAll(".product-card-usp-text");
  let uspsHtml = "";
  if (uspItems.length) {
    const items = [...uspItems].map((li, i) => {
      const text = esc(li.childNodes[0]?.textContent?.trim() || "");
      const sep =
        i < uspItems.length - 1
          ? '<span class="product-card-usp-text-separator" aria-hidden="true"> | </span>'
          : "";
      return `<li class="product-card-usp-text">${text}${sep}</li>`;
    });
    uspsHtml = `<ul class="product-card-usp-texts text-secondary-12-120 color-secondary">${items.join("")}</ul>`;
  }

  const description = placeholder.dataset.productDescription || "";
  const warmth = placeholder.dataset.warmth;
  const durability = placeholder.dataset.durability;
  const mobility = placeholder.dataset.mobility;

  let scalesHtml = "";
  if (warmth || durability || mobility) {
    const parts = [];
    if (warmth) parts.push(buildValueScale(Number(warmth), "Warmth"));
    if (durability)
      parts.push(buildValueScale(Number(durability), "Durability"));
    if (mobility) parts.push(buildValueScale(Number(mobility), "Mobility"));
    scalesHtml = `<div class="product-card-large-value-scales">${parts.join("")}</div>`;
  }

  const safeTitle = esc(title);
  const safeUrl = esc(url);
  const safePrice = esc(price);
  const safeDesc = esc(description);

  placeholder.innerHTML = `<product-card class="product-card-large" data-product-id="${esc(productId)}">
    <div class="product-media-wrapper">
      <a href="${safeUrl}" aria-label="${safeTitle}" tabindex="-1">${imageHtml}</a>
    </div>
    <div class="product-card-content-under-media">
      <a href="${safeUrl}" class="product-card-title-link">
        <h3 class="text-primary-20-120 product-card-large-title">${safeTitle}</h3>
      </a>
      <p class="product-card-price text-secondary-12-120 color-primary">
        <span class="product-card-price-value">${safePrice}</span>
      </p>
      ${uspsHtml}
      ${description ? `<p class="product-card-large-description text-secondary-14-140 color-primary">${safeDesc}</p>` : ""}
      ${scalesHtml}
      <div class="product-card-large-button-container">
        <a href="${safeUrl}" class="theme-button dark-theme"><span class="theme-button__label">View product</span></a>
      </div>
    </div>
  </product-card>`;

  placeholder.removeAttribute("data-deferred-presentation");
}

/**
 * The grid swaps to its desktop column counts at 768px, so content block
 * placement resolves against that breakpoint rather than `mediaQueryLarge`.
 */
const gridDesktopQuery = matchMedia("(min-width: 768px)");

/**
 * Grid geometry per view. On mobile a content block always spans the full width,
 * on desktop it spans the number of columns the merchant picked. The row lives in
 * a separate setting per desktop grid, so the density also decides which
 * attribute to read it from.
 *
 * Those attributes are read with `getAttribute` rather than `dataset`: a hyphen
 * before a digit survives the camelCase mapping, so `data-grid-columns-4` would
 * only be reachable as `dataset['gridColumns-4']`.
 */
const gridContentLayouts = {
  "default-mobile": {
    columns: 2,
    rowSpan: 1,
    row: "data-grid-row-mobile",
    blockColumns: () => 2,
  },
  "grid-mobile": {
    columns: 3,
    rowSpan: 1,
    row: "data-grid-row-mobile",
    blockColumns: () => 3,
  },
  "default-desktop": {
    columns: 4,
    rowSpan: 1,
    row: "data-grid-row-4",
    blockColumns: (block) =>
      Number(block.getAttribute("data-grid-columns-4")) || 2,
  },
  "grid-desktop": {
    columns: 6,
    rowSpan: 2,
    row: "data-grid-row-6",
    blockColumns: (block) =>
      Number(block.getAttribute("data-grid-columns-6")) || 3,
  },
};

/**
 * Enforces the placement rules the merchant's settings can break.
 *
 * A theme block only resolves its own settings, so Liquid can't tell whether two
 * content blocks compete for the same rows — it is settled here instead, once per
 * density, because a row that exists in the 4 column grid can sit past the end of
 * the shorter 6 column one. Earlier blocks win, so the later block is the one that
 * loses a row they both want.
 *
 * An offending block is dropped on the storefront. In the theme editor it stays
 * put and carries `data-grid-content-conflict`, which surfaces the reason — hiding
 * a block the merchant just placed would leave them nothing to act on.
 *
 * @param {Element} grid
 */
function resolveContentBlocks(grid) {
  const blocks = grid.querySelectorAll(".collection-grid-content");
  if (!blocks.length) return;

  const viewport = gridDesktopQuery.matches ? "desktop" : "mobile";
  const view = grid.getAttribute("product-grid-view") ?? "default";
  const layout = gridContentLayouts[`${view}-${viewport}`];

  // The single column views drop content blocks outright, in CSS.
  if (!layout) return;

  const productCells = grid.querySelectorAll(".product-grid__item").length;
  const occupiedRows = new Set();
  const adjacentRows = new Set();
  let blockCells = 0;

  for (const block of blocks) {
    // A block hidden on mobile leaves its row to the products, so it neither
    // claims rows nor conflicts with anything.
    if (viewport === "mobile" && block.hasAttribute("data-grid-hide-mobile")) {
      setContentBlockConflict(block, null);
      continue;
    }

    const firstRow = Math.max(1, Number(block.getAttribute(layout.row)) || 1);
    const lastRow = firstRow + layout.rowSpan - 1;
    const cells = layout.blockColumns(block) * layout.rowSpan;

    // Rows the grid ends up with once this block has taken its cells. Anything
    // past them would hang below the products instead of sitting among them.
    const gridRows = Math.ceil(
      (productCells + blockCells + cells) / layout.columns,
    );

    let conflict = null;
    for (let row = firstRow; row <= lastRow; row++) {
      if (occupiedRows.has(row)) conflict = "clash";
      else if (adjacentRows.has(row) && !conflict) conflict = "gap";
    }
    if (!conflict && lastRow > gridRows) conflict = "overflow";

    setContentBlockConflict(block, conflict);

    if (conflict) continue;

    blockCells += cells;
    for (let row = firstRow; row <= lastRow; row++) occupiedRows.add(row);
    // The design asks for at least one product row between two content blocks.
    adjacentRows.add(firstRow - 1);
    adjacentRows.add(lastRow + 1);
  }
}

/**
 * Flags a content block as unplaceable, or clears the flag.
 *
 * @param {Element} block
 * @param {'clash' | 'gap' | 'overflow' | null} conflict
 */
function setContentBlockConflict(block, conflict) {
  if (conflict) block.setAttribute("data-grid-content-conflict", conflict);
  else block.removeAttribute("data-grid-content-conflict");

  block.toggleAttribute(
    "data-grid-content-hidden",
    Boolean(conflict) && !window.Shopify?.designMode,
  );
}

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | null} */
  #gridObserver = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange);
    gridDesktopQuery.addEventListener("change", this.#resolveContentBlocks);
    this.setAttribute("initialized", "");

    const { grid } = this.refs;
    if (grid) {
      this.#gridObserver = new MutationObserver(() => {
        if (grid.getAttribute("product-grid-view") === "presentation") {
          this.#hydratePresentationCards();
        }
        // Products arriving from load more or a filter change alter how many rows
        // the grid has, which can make a dropped content block placeable again.
        resolveContentBlocks(grid);
      });
      this.#gridObserver.observe(grid, { childList: true });
      resolveContentBlocks(grid);
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener("change", this.#handleMediaQueryChange);
    gridDesktopQuery.removeEventListener("change", this.#resolveContentBlocks);
    this.#gridObserver?.disconnect();
  }

  #resolveContentBlocks = () => {
    const { grid } = this.refs;
    if (grid) resolveContentBlocks(grid);
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    if (value === "presentation") {
      this.#hydratePresentationCards();
    }

    await startViewTransition(() => this.#setLayout(value), ["product-grid"]);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? "desktop" : "mobile";
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute("product-grid-view", value);
    resolveContentBlocks(grid);
  }

  /**
   * Builds deferred presentation cards from existing DOM data.
   */
  #hydratePresentationCards() {
    const deferred = this.querySelectorAll("[data-deferred-presentation]");
    for (const el of deferred) {
      hydratePresentation(el);
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout("default");
  };
}

if (!customElements.get("results-list")) {
  customElements.define("results-list", ResultsList);
}
